import { ccmApi } from './api';

function patientName(patient) {
  const raw = patient?._raw || patient || {};
  return [raw.fname, raw.lname].filter(Boolean).join(' ') || patient?.name || raw.name || patient?.id || '';
}

function formatConsentValue(value) {
  if (!value) return 'pending';
  if (typeof value === 'string') return value;
  if (value.status) return value.status;
  if (value.signed) return 'signed';
  return 'pending';
}

function appendParts(parts) {
  return parts.filter(Boolean).join(' ');
}

export async function safeAudit(action, entityId, detail) {
  try {
    await ccmApi.auditEvent(action, 'patients', entityId, detail);
  } catch (e) {
    // Audit should not undo a completed patient write, but surface support signal.
    // eslint-disable-next-line no-console
    console.warn(`${action} audit failed:`, e);
  }
}

export async function auditConsentChange(patient, prevConsent, nextConsent, opts = {}) {
  if (!patient?.id) return;
  const mode = opts.mode ? ` (${opts.mode})` : '';
  const datePart = opts.date ? `— date ${opts.date}${opts.dateSource ? ` from ${opts.dateSource}` : ''}` : '';
  const reasonPart = opts.reason ? `— reason: ${opts.reason}` : '';
  const detail = appendParts([
    `Consent: ${formatConsentValue(prevConsent)} → ${formatConsentValue(nextConsent)}${mode}`,
    `for ${patientName(patient)}`,
    datePart,
    reasonPart,
  ]);
  await safeAudit('CONSENT_CHANGE', patient.id, detail);
}

export async function auditApcmConsentChange(patient, prevConsent, nextConsent, opts = {}) {
  if (!patient?.id) return;
  const prev = formatConsentValue(prevConsent);
  const next = formatConsentValue(nextConsent);
  const source = opts.source || nextConsent?.source || '';
  const mode = source ? ` (${source})` : '';
  const date = opts.date || nextConsent?.date || '';
  const datePart = date ? `— date ${date}${opts.dateSource ? ` from ${opts.dateSource}` : ''}` : '';
  const detail = appendParts([
    `APCM consent: ${prev} → ${next}${mode}`,
    `for ${patientName(patient)}`,
    datePart,
  ]);
  await safeAudit('APCM_CONSENT_CHANGE', patient.id, detail);
}

export async function auditBhiConsentChange(patient, prevConsent, nextConsent, opts = {}) {
  if (!patient?.id) return;
  const prev = formatConsentValue(prevConsent);
  const next = formatConsentValue(nextConsent);
  const source = opts.source || nextConsent?.source || '';
  const mode = source ? ` (${source})` : '';
  const date = opts.date || nextConsent?.date || '';
  const datePart = date ? `— date ${date}${opts.dateSource ? ` from ${opts.dateSource}` : ''}` : '';
  const detail = appendParts([
    `BHI consent: ${prev} → ${next}${mode}`,
    `for ${patientName(patient)}`,
    datePart,
  ]);
  await safeAudit('BHI_CONSENT_CHANGE', patient.id, detail);
}

// CPM consent + operational attestations. Detail carries the FULL substance —
// initiating visit (date + practitioner) and the disclosures — because these
// are billing-integrity facts an auditor asks for (CPM plan P0.2).
export async function auditPainConsentChange(patient, painRecord, opts = {}) {
  if (!patient?.id) return;
  const c = (painRecord && painRecord.consent) || {};
  const iv = (painRecord && painRecord.initiatingVisit) || {};
  const bp = (painRecord && painRecord.billingProvider) || {};
  const detail = appendParts([
    `CPM consent: ${c.signed ? 'signed' : 'pending'} (${c.mode || 'verbal'})`,
    `for ${patientName(patient)}`,
    c.date ? `— consent date ${c.date}` : '',
    iv.date ? `— initiating in-person visit ${iv.date} by ${iv.practitionerName || 'unrecorded'}` : '',
    bp.name ? `— designated CPM billing provider ${bp.name}` : '',
    c.disclosures ? '— patient informed: Medicare coinsurance applies; may discontinue at any time' : '',
    opts.note ? `— ${opts.note}` : '',
  ]);
  await safeAudit('PAIN_CONSENT_CHANGE', patient.id, detail);
}

// PIN consent + enrollment attestations. Only persisted facts are included;
// the navigator model is an auxiliary-personnel attestation, not an individual.
export async function auditPinConsentChange(patient, pinRecord, opts = {}) {
  if (!patient?.id) return;
  const c = (pinRecord && pinRecord.consent) || {};
  const iv = (pinRecord && pinRecord.initiatingVisit) || {};
  const training = (pinRecord && pinRecord.navigatorTraining) || {};
  const condition = String((pinRecord && pinRecord.qualifyingCondition) || '').trim();
  const detail = appendParts([
    `PIN consent: ${c.signed ? 'signed' : 'pending'} (${c.mode || 'verbal'})`,
    `for ${patientName(patient)}`,
    c.date ? `— consent date ${c.date}` : '',
    condition ? `— qualifying condition ${condition}` : '',
    iv.date ? `— initiating visit ${iv.date}${iv.practitionerName ? ` by ${iv.practitionerName}` : ''}` : '',
    training.attested === true ? '— navigator training documentation attested' : '',
    c.disclosures ? '— patient informed: Medicare coinsurance applies; may discontinue at any time' : '',
    opts.note ? `— ${opts.note}` : '',
  ]);
  await safeAudit('PIN_CONSENT_CHANGE', patient.id, detail);
}
